import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Plotly from 'plotly.js-dist-min';

const API_BASE_URL = (
    typeof process !== 'undefined' &&
    process.env &&
    process.env.REACT_APP_API_BASE_URL
) || 'http://52.66.78.81:4000';

// Helper functions (remain the same)
const parseTimestamp = (timestampStr) => {
    if (!timestampStr) return null;
    const parts = timestampStr.split(' ');
    if (parts.length !== 2) return null;
    const dateParts = parts[0].split('-');
    if (dateParts.length !== 3) return null;
    const [day, month, year] = dateParts;
    const isoStr = `${year}-${month}-${day}T${parts[1]}`;
    return new Date(isoStr);
};

const parseCSV = (csvText) => {
    const lines = csvText.trim().split(/\r?\n/);
    if (lines.length < 2) return []; // Return empty array if no data
    const header = lines[0].split(',').map(h => h.trim());
    return lines.slice(1).map(line => {
        const values = line.split(',');
        let row = {};
        header.forEach((h, i) => {
            row[h] = values[i];
        });
        return row;
    });
};

const SeismicGraphViewer = () => {
    const { '*': filePath } = useParams();
    const decodedFilePath = decodeURIComponent(filePath);
    const [graphData, setGraphData] = useState({ timestamps: [], x: [], y: [], z: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const graphEl = useRef(null);
    const loggedInUser = localStorage.getItem('waivsUser') || "admin";
    const authToken = localStorage.getItem('waivsAuthToken') || "";

    useEffect(() => {
        if (!decodedFilePath) return;

        const fetchData = async () => {
            setIsLoading(true);
            setError('');
            setStatusMessage('');
            try {
                const response = await fetch(`${API_BASE_URL}/api/data/download`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ user: loggedInUser, token: authToken, file: decodedFilePath })
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch data file. Status: ${response.status}`);
                }

                const csvText = await response.text();
                const parsedData = parseCSV(csvText);

                if (parsedData.length === 0) {
                    setStatusMessage("The CSV file is empty or contains only a header.");
                    setGraphData({ timestamps: [], x: [], y: [], z: [] });
                    return; // Stop processing
                }

                // --- START: ROBUST DATA VALIDATION ---
                const validDataPoints = [];
                parsedData.forEach((row, index) => {
                    const timestamp = parseTimestamp(row.Timestamp);
                    const x = parseFloat(row.X);
                    const y = parseFloat(row.Y);
                    const z = parseFloat(row.Z);

                    // Check if all values in the row are valid
                    if (timestamp && !isNaN(x) && !isNaN(y) && !isNaN(z)) {
                        validDataPoints.push({ timestamp, x, y, z });
                    } else {
                        // Log the problematic row for debugging
                        console.warn(`Skipping invalid row #${index + 2}:`, row);
                    }
                });
                // --- END: ROBUST DATA VALIDATION ---

                if (validDataPoints.length === 0) {
                    setStatusMessage("No valid data points could be found in this file to generate a graph.");
                    setGraphData({ timestamps: [], x: [], y: [], z: [] });
                    return; // Stop processing
                }
                
                // Map the validated data into separate arrays for Plotly
                const timestamps = validDataPoints.map(d => d.timestamp);
                const x = validDataPoints.map(d => d.x);
                const y = validDataPoints.map(d => d.y);
                const z = validDataPoints.map(d => d.z);

                setGraphData({ timestamps, x, y, z });

            } catch (err) {
                console.error("Error fetching or parsing CSV:", err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [decodedFilePath, loggedInUser, authToken]);

    useEffect(() => {
        // Clear the previous graph when loading new data
        if (isLoading && graphEl.current) {
            Plotly.purge(graphEl.current);
        }
        
        if (!graphEl.current || isLoading || error || graphData.timestamps.length === 0) {
            return;
        }

        const traces = [
            { x: graphData.timestamps, y: graphData.x, mode: 'lines', name: 'X Accel', line: { color: '#1f77b4' } },
            { x: graphData.timestamps, y: graphData.y, mode: 'lines', name: 'Y Accel', line: { color: '#ff7f0e' } },
            { x: graphData.timestamps, y: graphData.z, mode: 'lines', name: 'Z Accel', line: { color: '#2ca02c' } },
        ];
        const layout = {
            title: `Seismic Waveform: ${decodedFilePath}`,
            xaxis: { title: 'Time', type: 'date' },
            yaxis: { title: 'Acceleration (Gal)' },
            margin: { t: 50, l: 60, r: 30, b: 50 },
            legend: { x: 1, xanchor: 'right', y: 1 }
        };

        Plotly.react(graphEl.current, traces, layout);
    }, [graphData, isLoading, error, decodedFilePath]);


    return (
        <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
            <button 
                onClick={() => window.close()} 
                style={{ 
                    textDecoration: 'none', padding: '8px 12px', backgroundColor: '#718096', 
                    color: 'white', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.9rem'
                }}
            >
                &larr; Close Page
            </button>
            
            <h1 style={{ textAlign: 'center' }}>Historical Waveform Viewer</h1>
            
            {/* Display status messages clearly */}
            <div style={{textAlign: 'center', minHeight: '24px'}}>
                {isLoading && <p>Loading graph data...</p>}
                {error && <p style={{ color: '#c53030', fontWeight: 'bold' }}>Error: {error}</p>}
                {statusMessage && <p>{statusMessage}</p>}
            </div>

            <div ref={graphEl} style={{ width: '100%', height: 'calc(100vh - 150px)', marginTop: '20px' }} />
        </div>
    );
};

export default SeismicGraphViewer;
